import { HttpClient } from '@angular/common/http'
import { computed, DestroyRef, inject, Signal, signal } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { injectQuery } from '@ngneat/query'
import { distinctUntilChanged, of, Subscription, switchMap } from 'rxjs'
import { ListFilterValues, ListParameterQuery, ListParameterValue, ListQueryResponse, toListHttpParams } from '../list-query-options'
import { LoggerService } from '../logger.service'
import { isEqual } from '../utils'

/** A GET endpoint that implements the list contract: `{ list, total }` for page/sort/filter params. */
export type AppTableEndpoint = {
  url: string
  /**
   * Extra query params sent with every request, e.g. a scope the table itself knows nothing about.
   * A list value is sent as one pair per entry. Rebind the whole endpoint to change these: the table
   * refetches when the source it was given changes.
   */
  params?: Record<string, ListParameterValue>
  /**
   * Cache key prefix. Pass the resource's own key so existing mutations' `invalidateQueries` refresh
   * the table and its facet lists; omit it and the table caches under `['app-table', url]` and goes
   * stale after a write.
   */
  queryKey?: unknown[]
  /** Only needed when the facets route is not `${url}/facets`. */
  facetsUrl?: string
}

export type AppTableSource<T> = T[] | AppTableEndpoint

export type AppTableFetchStatus = 'loading' | 'success' | 'error'

/** A column's fetched option list: every value the column could hold, searched client-side. */
export type AppTableFacet = { status: AppTableFetchStatus; values: (string | number)[] }

export type AppTableFetcher<T> = {
  rows: Signal<T[]>
  total: Signal<number>
  status: Signal<AppTableFetchStatus>
  isFetching: Signal<boolean>
  /** Increments on every arriving page, including one whose rows are identical to the last. */
  loadCount: Signal<number>
  retry: () => void
}

export type AppTableFacetFetcher = {
  facets: Signal<Record<string, AppTableFacet>>
  loadFacet: (endpoint: AppTableEndpoint, field: string) => void
  clearFacets: () => void
}

// The option list carries no filter, so it only moves when the column's own set of possible values
// does, and a mutation invalidates it through the shared query key anyway.
const FACET_STALE_TIME = 30 * 60 * 1000

export function isEndpointSource<T>(source: AppTableSource<T>): source is AppTableEndpoint {
  return !Array.isArray(source)
}

function resolveQueryKey(endpoint: AppTableEndpoint): unknown[] {
  return endpoint.queryKey ?? ['app-table', endpoint.url]
}

function resolveFacetsUrl(endpoint: AppTableEndpoint): string {
  return endpoint.facetsUrl ?? `${endpoint.url}/facets`
}

const collator = new Intl.Collator()

/** Numbers compare numerically so a facet list of ids is not ordered 1, 10, 2. */
export function compareFacetValues(left: unknown, right: unknown): number {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }
  return collator.compare(String(left), String(right))
}

/**
 * Fetches a page of rows whenever the endpoint, list query or filters change.
 *
 * The observable boundary is load-bearing: the table's options read `total()`, `total` is written
 * here, and this fetch is driven by the table's own state. Writing through a subscription makes that
 * write asynchronous and breaks what would otherwise be a signal cycle.
 */
export function createTableFetcher<T>(
  endpoint: Signal<AppTableEndpoint | null>,
  listQuery: Signal<ListParameterQuery>,
  filters: Signal<Record<string, ListFilterValues>>
): AppTableFetcher<T> {
  const query = injectQuery()
  const httpClient = inject(HttpClient)
  const destroyRef = inject(DestroyRef)

  const rows = signal<T[]>([])
  const total = signal(0)
  const status = signal<AppTableFetchStatus>('loading')
  const isFetching = signal(false)
  const loadCount = signal(0)
  const attempt = signal(0)

  const request = computed(() => ({ endpoint: endpoint(), listQuery: listQuery(), filters: filters(), attempt: attempt() }))

  toObservable(request)
    .pipe(
      distinctUntilChanged(isEqual),
      switchMap((current) => {
        if (current.endpoint === null) {
          return of(null)
        }
        const target = current.endpoint
        return query({
          queryKey: [...resolveQueryKey(target), current.listQuery, current.filters, target.params ?? {}],
          queryFn: () => httpClient.get<ListQueryResponse<T>>(target.url, { params: toListHttpParams(current.listQuery, current.filters, target.params) }),
        }).result$
      }),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe((result) => {
      if (result === null) {
        return
      }
      isFetching.set(result.isFetching)
      if (result.isError) {
        status.set('error')
        return
      }
      if (result.data === undefined) {
        status.set('loading')
        return
      }
      rows.set(result.data.list)
      total.set(result.data.total)
      status.set('success')
      loadCount.update((count) => count + 1)
    })

  return { rows, total, status, isFetching, loadCount, retry: () => attempt.update((count) => count + 1) }
}

/**
 * Fetches every value a facet column could hold on first open, cached per column. The request carries
 * only `field` — no filters, no search term, no limit — so every user of a column shares one cache
 * entry and typing in the popover costs no requests.
 */
export function createFacetFetcher(): AppTableFacetFetcher {
  const query = injectQuery()
  const httpClient = inject(HttpClient)
  const destroyRef = inject(DestroyRef)
  const logger = inject(LoggerService)

  const facets = signal<Record<string, AppTableFacet>>({})
  // One live subscription per column, replaced rather than stacked, so a retry or a new source does not
  // leave the previous query subscribed and still writing.
  const subscriptions = new Map<string, Subscription>()

  function setFacet(field: string, facet: AppTableFacet) {
    facets.update((current) => ({ ...current, [field]: facet }))
  }

  function clearFacets() {
    subscriptions.forEach((subscription) => subscription.unsubscribe())
    subscriptions.clear()
    facets.set({})
  }

  destroyRef.onDestroy(clearFacets)

  function loadFacet(endpoint: AppTableEndpoint, field: string) {
    const existing = facets()[field]
    if (existing !== undefined && existing.status !== 'error') {
      return
    }
    const facetsUrl = resolveFacetsUrl(endpoint)
    subscriptions.get(field)?.unsubscribe()
    setFacet(field, { status: 'loading', values: [] })
    const subscription = query({
      queryKey: [...resolveQueryKey(endpoint), 'facets', field],
      queryFn: () => httpClient.get<{ values: (string | number)[] }>(facetsUrl, { params: { field } }),
      staleTime: FACET_STALE_TIME,
    }).result$.subscribe((result) => {
      if (result.isError) {
        // A facet column without a working facets route would otherwise render an empty checklist
        // that reads as "this column has no values".
        logger.error(`Could not load the "${field}" facet values from ${facetsUrl}.`, result.error)
        setFacet(field, { status: 'error', values: [] })
        return
      }
      if (result.data !== undefined) {
        // Sorted once here rather than on every read of the popover.
        setFacet(field, { status: 'success', values: [...result.data.values].sort(compareFacetValues) })
      }
    })
    subscriptions.set(field, subscription)
  }

  return { facets, loadFacet, clearFacets }
}
